import { useState } from 'react';
import { ExternalLink, Heart, IndianRupee, Image, Instagram, MessageSquare, MousePointer, Rocket, TrendingUp, Users } from 'lucide-react';
import { AreaChart } from '@tremor/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { PostPreview, type PostPreviewData } from '@/components/PostPreview';
import { imgProxy } from '@/lib/img';
import {
  useMetaAccountSnapshot,
  useMetaAdCampaigns,
  useMetaBrandDaily,
  useMetaPosts,
  type MetaPost,
} from '@/hooks/useMeta';
import { formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

export function MetaPage() {
  const ig = useMetaBrandDaily('instagram');
  const fb = useMetaBrandDaily('facebook');
  const ads = useMetaBrandDaily('facebook_ads');
  const snapshots = useMetaAccountSnapshot();
  const campaigns = useMetaAdCampaigns();
  const posts = useMetaPosts(10);
  const [preview, setPreview] = useState<PostPreviewData | null>(null);

  const igSnapshot = snapshots.data?.find((s) => s.provider === 'instagram');
  const igDaily = ig.data ?? [];
  const fbDaily = fb.data ?? [];
  const adsDaily = ads.data ?? [];

  const igTotalReach = igDaily.reduce((s, r) => s + (r.reach ?? 0), 0);
  const igTotalProfileViews = igDaily.reduce((s, r) => s + (r.profile_views ?? 0), 0);
  const igNewFollowers = igDaily.reduce((s, r) => s + (r.new_followers ?? 0), 0);
  const fbTotalReach = fbDaily.reduce((s, r) => s + (r.reach ?? 0), 0);
  const adsTotalSpend = adsDaily.reduce((s, r) => s + (Number(r.spend) || 0), 0);
  const adsTotalImpressions = adsDaily.reduce((s, r) => s + (r.impressions ?? 0), 0);
  const adsTotalClicks = adsDaily.reduce((s, r) => s + (r.clicks ?? 0), 0);
  const adsAvgCtr = adsTotalImpressions > 0 ? (adsTotalClicks / adsTotalImpressions) * 100 : 0;

  // Period range — pull the lowest/highest dates from any series.
  const allDates = [...igDaily, ...fbDaily, ...adsDaily].map((d) => d.date).sort();
  const earliest = allDates[0];
  const latest = allDates[allDates.length - 1];

  const loading = ig.isLoading || fb.isLoading || ads.isLoading || snapshots.isLoading;

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Instagram className="h-5 w-5 text-rose-500" />
            Meta — Instagram, Facebook, Ads
          </span>
        }
        description={
          earliest && latest
            ? `Brand-level performance · ${formatDateRange(earliest, latest)}`
            : 'Brand-level performance'
        }
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SummaryTile
          label="IG followers"
          value={igSnapshot?.followers_count ? formatNumberCompact(igSnapshot.followers_count) : '—'}
          icon={<Users className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="IG reach (28d)"
          value={formatNumberCompact(igTotalReach)}
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="New IG followers"
          value={formatNumberCompact(igNewFollowers)}
          icon={<Users className="h-3.5 w-3.5" />}
          loading={loading}
        />
        <SummaryTile
          label="Ads spend (28d)"
          value={formatINRCompact(adsTotalSpend)}
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <MotionCard>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Instagram className="h-4 w-4 text-rose-500" /> Instagram reach
                <span className="text-2xs font-normal text-muted-foreground">daily</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ig.isLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : igDaily.length === 0 ? (
                <EmptyState title="No IG data" description="No reach data for this period." />
              ) : (
                <AreaChart
                  data={igDaily.map((d) => ({ date: formatChartDate(d.date), reach: d.reach ?? 0 }))}
                  index="date"
                  categories={['reach']}
                  colors={['rose']}
                  valueFormatter={formatNumberCompact}
                  showGridLines={false}
                  showLegend={false}
                  showAnimation
                  curveType="monotone"
                  className="h-64"
                />
              )}
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-muted-foreground" /> FB Ads spend
                <span className="text-2xs font-normal text-muted-foreground">daily</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ads.isLoading ? (
                <Skeleton className="h-64 w-full" />
              ) : adsDaily.length === 0 ? (
                <EmptyState title="No ads data" description="No ad spend for this period." />
              ) : (
                <AreaChart
                  data={adsDaily.map((d) => ({
                    date: formatChartDate(d.date),
                    spend: Number(d.spend) || 0,
                  }))}
                  index="date"
                  categories={['spend']}
                  colors={['emerald']}
                  valueFormatter={formatINRCompact}
                  showGridLines={false}
                  showLegend={false}
                  showAnimation
                  curveType="monotone"
                  className="h-64"
                />
              )}
            </CardContent>
          </Card>
        </MotionCard>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <MotionCard>
          <Card>
            <CardHeader>
              <CardTitle>FB Page reach</CardTitle>
            </CardHeader>
            <CardContent>
              {fbDaily.length === 0 ? (
                <EmptyState title="No FB data" description="Page mostly dormant in this period." />
              ) : (
                <ul className="space-y-1.5 text-xs">
                  <li className="flex justify-between border-b border-border/40 py-1">
                    <span className="text-muted-foreground">Total reach (28d)</span>
                    <span className="font-mono">{formatNumberCompact(fbTotalReach)}</span>
                  </li>
                  <li className="flex justify-between border-b border-border/40 py-1">
                    <span className="text-muted-foreground">Peak day</span>
                    <span className="font-mono">
                      {formatNumberCompact(Math.max(...fbDaily.map((d) => d.reach ?? 0)))}
                    </span>
                  </li>
                  <li className="flex justify-between py-1">
                    <span className="text-muted-foreground">Days with reach &gt; 100</span>
                    <span className="font-mono">
                      {fbDaily.filter((d) => (d.reach ?? 0) > 100).length} of {fbDaily.length}
                    </span>
                  </li>
                </ul>
              )}
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader>
              <CardTitle>IG summary (28d)</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5 text-xs">
                <SummaryRow label="Total reach" value={formatNumberCompact(igTotalReach)} />
                <SummaryRow label="Profile views" value={formatNumberCompact(igTotalProfileViews)} />
                <SummaryRow label="New followers" value={formatNumberCompact(igNewFollowers)} />
                <SummaryRow label="Posts" value={`${posts.data?.length ?? 0}`} />
              </ul>
            </CardContent>
          </Card>
        </MotionCard>
        <MotionCard delay={0.1}>
          <Card>
            <CardHeader>
              <CardTitle>Ads summary (28d)</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5 text-xs">
                <SummaryRow label="Spend" value={formatINRCompact(adsTotalSpend)} />
                <SummaryRow label="Impressions" value={formatNumberCompact(adsTotalImpressions)} />
                <SummaryRow label="Clicks" value={formatNumberCompact(adsTotalClicks)} />
                <SummaryRow label="Avg CTR" value={`${adsAvgCtr.toFixed(2)}%`} />
              </ul>
            </CardContent>
          </Card>
        </MotionCard>
      </div>

      <MotionCard delay={0.1}>
        <Card>
          <CardHeader>
            <CardTitle>FB Ads campaigns</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {campaigns.isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (campaigns.data ?? []).length === 0 ? (
              <div className="p-6">
                <EmptyState title="No campaigns" description="No active ad campaigns for this period." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Campaign / Ad set</th>
                      <th className="px-3 py-2 text-right font-medium">Spend</th>
                      <th className="px-3 py-2 text-right font-medium">Impressions</th>
                      <th className="px-3 py-2 text-right font-medium">Reach</th>
                      <th className="px-3 py-2 text-right font-medium">Clicks</th>
                      <th className="px-3 py-2 text-right font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {campaigns.data?.map((c) => (
                      <tr key={c.campaign_id} className="hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <div className="font-medium leading-tight">{c.campaign_name}</div>
                          <div className="mt-0.5 text-2xs text-muted-foreground">{c.account_name}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatINRCompact(Number(c.spend) || 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatNumberCompact(c.impressions ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatNumberCompact(c.reach ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {formatNumberCompact(c.clicks ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {((c.ctr ?? 0) * 100).toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </MotionCard>

      <MotionCard delay={0.15}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="h-4 w-4" /> Top IG posts (28d)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {posts.isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (posts.data ?? []).length === 0 ? (
              <div className="p-6">
                <EmptyState title="No posts" description="No IG posts found for this period." />
              </div>
            ) : (
              <ul className="divide-y divide-border/40">
                {[...(posts.data ?? [])]
                  .sort((a, b) => (b.reach ?? 0) - (a.reach ?? 0))
                  .map((p) => (
                    <PostRow
                      key={p.media_id}
                      post={p}
                      onSelect={() =>
                        setPreview({
                          id: p.media_id,
                          caption: p.caption,
                          permalink: p.permalink,
                          imageUrl: p.image_url,
                          thumbnailUrl: p.thumbnail_url,
                          mediaType: p.media_type,
                          postedAt: p.published_at,
                          likes: p.like_count,
                          comments: p.comments_count,
                          views: p.views,
                          reach: p.reach,
                          isBoosted: p.is_boosted,
                          source: '@flaxitup',
                        })
                      }
                    />
                  ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </MotionCard>

      <PostPreview post={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  icon,
  loading,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-1.5 text-2xs font-normal text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="font-semibold tabular-nums text-2xl">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="flex justify-between border-b border-border/40 py-1 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </li>
  );
}

function formatChartDate(iso: string): string {
  // IST short label, e.g. "May 12"
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  const e = new Date(end).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

function PostRow({ post, onSelect }: { post: MetaPost; onSelect: () => void }) {
  const caption = ((post.caption ?? '').split('\n')[0] ?? '').slice(0, 140);
  const date = post.published_at ? new Date(post.published_at).toLocaleDateString('en-IN') : '';
  // image_url is the actual photo/first-frame; thumbnail_url is only set for videos.
  // Prefer thumbnail (smaller) for videos, image_url for everything else.
  const src = post.media_type === 'VIDEO' ? (post.thumbnail_url || post.image_url) : (post.image_url || post.thumbnail_url);
  return (
    <li className="p-4">
      <div className="flex gap-3">
        {src ? (
          <button
            type="button"
            onClick={onSelect}
            className="block h-20 w-20 shrink-0 overflow-hidden rounded-md border border-border/40"
          >
            <img
              src={imgProxy(src)}
              alt=""
              className="h-full w-full object-cover transition-transform hover:scale-105"
              loading="lazy"
            />
          </button>
        ) : (
          <div
            className={cn(
              'flex h-20 w-20 shrink-0 items-center justify-center rounded-md',
              post.media_type === 'VIDEO' && 'bg-violet-500/15',
              post.media_type === 'IMAGE' && 'bg-sky-500/15',
              post.media_type === 'CAROUSEL_ALBUM' && 'bg-rose-500/15',
            )}
          >
            <Image className="h-5 w-5 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground">
            <Badge variant="muted" className="text-2xs">
              {post.media_type === 'VIDEO' ? 'Reel' : post.media_type === 'IMAGE' ? 'Post' : 'Carousel'}
            </Badge>
            {post.is_boosted && (
              <Badge variant="muted" className="bg-sky-500/15 text-2xs text-sky-600 dark:text-sky-400">
                <Rocket className="mr-1 h-3 w-3" />
                Boosted
              </Badge>
            )}
            <span>{date}</span>
            {post.permalink && (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 hover:text-foreground hover:underline"
              >
                Open <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </div>
          <div className="mt-1 truncate text-sm text-foreground/90">{caption}</div>
          <div className="mt-1.5 flex flex-wrap gap-3 text-2xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <TrendingUp className="h-3 w-3" /> {formatNumberCompact(post.reach ?? 0)} reach
            </span>
            <span className="inline-flex items-center gap-1">
              <MousePointer className="h-3 w-3" /> {formatNumberCompact(post.views ?? 0)} views
            </span>
            <span className="inline-flex items-center gap-1">
              <Heart className="h-3 w-3" /> {formatNumberCompact(post.like_count ?? 0)}
            </span>
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {formatNumberCompact(post.comments_count ?? 0)}
            </span>
          </div>
        </div>
      </div>
    </li>
  );
}
